import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ISwapTradeFees } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import { useTheme } from "next-themes";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { calculateCashAtDataLevel } from "../cellRenderers/CashCellRenderer";

// LQC-908: Block-level Unwind Amount must be strictly less than 9× nominal in absolute value.
export const UNWIND_AMOUNT_NOMINAL_MULTIPLIER = 9;

//NOTE: When a new fee is entered the code needs to also update the direction to a 'user input' otherwise the code may later ready the wrong fee field.
export function updateUserInputDirection(feeObj: ISwapTradeFees) {
  if (feeObj.userUpdatedDirection) {
    return feeObj.userUpdatedDirection;
    // 0 is a valid entered fee; only fall back to indicative when nothing was entered.
  } else if (feeObj.transactionFee != null) {
    return feeObj.transactionFeeDirection;
  } else {
    return feeObj.indicativeFeeDirection;
  }
}

// LQC-908: pure check used by the leg-level branch of validateAndSave.
// Only NPV / Recoupon blocks are gated; ER-level NPV-style trades are intentionally not validated.
export function exceedsUnwindAmountLimit(
  value: string | number | null | undefined,
  leg: ExecutionRequestQuoteLeg | undefined,
): boolean {
  if (!leg) return false;
  if (leg.unwind !== "NPV" && leg.unwind !== "Recoupon") return false;
  if (value === "" || value == null) return false;

  const numericValue = Number(value);
  const nominal = Number(leg.notionalLeg1);
  if (
    !Number.isFinite(numericValue) ||
    !Number.isFinite(nominal) ||
    nominal <= 0
  ) {
    return false;
  }
  return Math.abs(numericValue) >= nominal * UNWIND_AMOUNT_NOMINAL_MULTIPLIER;
}

const CashCellEditor = forwardRef((params: any, ref) => {
  const { theme } = useTheme();
  const { fullTradeData, isOp, isLeg, isER, isOutright } =
    returnAllTradeDetailsFromGenericParams(params);
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid == fullTradeData.esid,
  );
  const [value, setValue] = useState(
    thisTrade
      ? calculateCashAtDataLevel(
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        )
      : "",
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Strip commas only so pasted values like "8,040,000,000" parse cleanly.
    // Anything else that doesn't parse to a finite number is caught by the NaN guard in validateAndSave.
    const cleaned = event.target.value.replace(/,/g, "");
    setValue(cleaned);
  };

  const validateAndSave = () => {
    if (value !== "" && !Number.isFinite(Number(value))) {
      toast.error("Cash value is not a valid number");
      return calculateCashAtDataLevel(
        params,
        thisTrade!,
        isOp,
        isLeg,
        isER,
        isOutright,
      );
    }
    if (Number(value) < 0) {
      toast.error("Cash value cannot be a negative");
      return 0;
    }

    if (thisTrade) {
      if (!isOp && isER && !isOutright) {
        //RP ER
        if (!thisTrade.fees) {
          return;
        }
        const updatedFees = thisTrade!.fees.map((feeObj) => {
          // nested in if(thisTrade)
          return feeObj.transactionFeeSide == thisTrade!.quoteSideER
            ? {
                ...feeObj,
                transactionFee: value != "" ? Number(value) : null,
                userUpdatedDirection: updateUserInputDirection(feeObj), // Wont use the above transactionFee - Instead will ues the original values.
                // Meaning, if there is no current transactionFee it will take the indicative direction (ensuring never to flip the direction in the case of indicative and transaction fees having different directions.)
              }
            : { ...feeObj };
        });
        const newData = currentTradeData.map((trade) => {
          return trade.esid == fullTradeData.esid
            ? {
                ...trade,
                fees: updatedFees,
              }
            : trade;
        });
        tradeDataStore.getState().updateTradeData(newData);
      } else if (
        (!isOp && isLeg && thisTrade.legs) ||
        (!isOp && isOutright && thisTrade.legs)
      ) {
        if (!thisTrade.legs || thisTrade.legs.length < 0) {
          return;
        }
        const blockIdToCheck = !isOutright
          ? params.data.blockId
          : params.data.legs?.[0]?.blockId;

        // LQC-908: block/leg-level Unwind Amount must be < 9× nominal (NPV / Recoupon only).
        // Skip ER-level NPV-style trades — only the leg branch validates.
        // On rejection we null the leg's transactionFee on the user's quoteSide so
        // the cell renders empty and the existing missing-cash gate in
        // quoteTradeStoreAction blocks the next quote — instead of silently
        // reverting to the prior accepted value.
        if (isLeg || isOutright) {
          const targetLeg = thisTrade.legs.find(
            (leg) => leg.blockId == blockIdToCheck,
          );
          if (exceedsUnwindAmountLimit(value, targetLeg) && targetLeg) {
            toast.error(
              "Value is too large. Please enter a smaller value.",
            );
            tradeSettingsStore.getState().updateLegCellsToFlash([
              {
                esid: fullTradeData.esid,
                blockId: targetLeg.blockId,
                field: "cash",
              },
            ]);
            setTimeout(() => {
              tradeSettingsStore.getState().clearLegCellsToFlash();
            }, 3000);
            const clearedLegs = thisTrade.legs.map((leg) =>
              leg.blockId == targetLeg.blockId
                ? {
                    ...leg,
                    fees: leg.fees!.map((feeObj) =>
                      feeObj.transactionFeeSide == leg.quoteSide
                        ? { ...feeObj, transactionFee: null }
                        : { ...feeObj },
                    ),
                  }
                : leg,
            );
            const clearedTradeData = currentTradeData.map((trade) =>
              trade.esid == fullTradeData.esid
                ? { ...thisTrade, legs: clearedLegs }
                : trade,
            );
            tradeDataStore.getState().updateTradeData(clearedTradeData);
            return "";
          }
        }

        const newThisTradeLegs = thisTrade.legs.map((leg) => {
          return leg.blockId == blockIdToCheck
            ? {
                ...leg,
                fees: leg.fees!.map((feeObj) =>
                  feeObj.transactionFeeSide == leg.quoteSide
                    ? {
                        ...feeObj,
                        originalTransactionFee:
                          !feeObj.transactionFeeHasBeenUpdated
                            ? feeObj.transactionFee
                            : feeObj.originalTransactionFee,
                        transactionFee: value != "" ? Number(value) : null, // Mapping the nested data on the nested legs.
                        userUpdatedDirection: updateUserInputDirection(feeObj), // Wont use the above transactionFee - Instead will ues the original values.
                        // Meaning, if there is no current transactionFee it will take the indicative direction (ensuring never to flip the direction in the case of indicative and transaction fees having different directions.)
                        transactionFeeHasBeenUpdated: true,
                      }
                    : { ...feeObj },
                ),
              }
            : leg;
        });
        const newThisTrade = { ...thisTrade, legs: newThisTradeLegs };
        const newData = currentTradeData.map((trade) => {
          return trade.esid == fullTradeData.esid
            ? {
                ...newThisTrade,
              }
            : trade;
        });
        tradeDataStore.getState().updateTradeData(newData);
      }
    }
    if (value == "") {
      return value;
    }
    return Number(value);
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  return (
    <input
      ref={inputRef}
      type=""
      value={value != null && value !== "" ? value.toString() : ""}
      onChange={handleInput} // Blocks unwanted characters immediately
      className={`${
        theme == "light"
          ? "inputCellOtherLight inputCellLight"
          : "inputCellOtherDark inputCellDark"
      } ag-input ag-roundedInputStyle w-full justify-center !border-none text-center`}
    />
  );
});

export default CashCellEditor;
